import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-weather',
    category: 'data',
    title: 'File meteo',
    summary: 'File opzionali che migliorano l\'accuratezza delle previsioni solari ed eoliche.',
    body: (
      <>
        <p>
          I file meteo sono opzionali ma migliorano l'accuratezza delle previsioni. Esistono due tipi.
        </p>
        <h5 className="mt-3">Meteo solare</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonna</th><th>Obbligatoria?</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Obbligatoria</td><td>L'ora</td></tr>
            <tr><td><Code>ghi_w_m2</Code></td><td>Obbligatoria</td><td>Intensità della luce solare, in watt per metro quadro</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Opzionale</td><td>Temperatura dell'aria, °C</td></tr>
            <tr><td><Code>cloud_cover_pct</Code></td><td>Opzionale</td><td>Copertura nuvolosa, 0–100%</td></tr>
            <tr><td><Code>dni_w_m2</Code></td><td>Opzionale</td><td>Luce solare diretta, W/m²</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_solar">Template meteo solare</TemplateBtn>

        <h5 className="mt-4">Meteo eolico</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonna</th><th>Obbligatoria?</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Obbligatoria</td><td>L'ora</td></tr>
            <tr><td><Code>wind_speed_m_s</Code></td><td>Obbligatoria</td><td>Velocità del vento, in metri al secondo</td></tr>
            <tr><td><Code>wind_direction_deg</Code></td><td>Opzionale</td><td>Direzione del vento, 0–360°</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Opzionale</td><td>Temperatura dell'aria, °C</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_wind">Template meteo eolico</TemplateBtn>
        <Tip>Le colonne opzionali possono essere omesse del tutto — ma se si include una colonna, compilarla per ogni riga.</Tip>

        <h5 className="mt-4">Dove posso trovare i dati meteo?</h5>
        <p>
          Quasi mai è necessario rilevare questi dati personalmente. Diversi servizi gratuiti pubblicano
          dati meteo orari storici per qualsiasi località in Europa. Inizia con quelli universali —
          coprono tutti i paesi pilota e non richiedono registrazione.
        </p>

        <h6 className="mt-3">Open-Meteo (consigliato, il più semplice)</h6>
        <p>
          <Ext href="https://open-meteo.com/en/docs/historical-weather-api">open-meteo.com/en/docs/historical-weather-api</Ext>
          {' '}— gratuito, senza registrazione, dati orari storici dal 1940.
        </p>
        <ol>
          <li>Apri la pagina indicata. Nel modulo, inserisci la <strong>latitudine e la
            longitudine</strong> della tua comunità (cercale su Google Maps — clic destro sulla posizione → «What's here»).</li>
          <li>Imposta la <strong>Data inizio</strong> e la <strong>Data fine</strong> per l'anno desiderato.</li>
          <li>In <strong>Hourly Variables</strong>, seleziona le variabili seguenti (salta l'elenco eolico se
            hai bisogno solo di dati solari, e viceversa):
            <ul>
              <li><Code>shortwave_radiation</Code> — corrisponde a GHI (in W/m²).</li>
              <li><Code>direct_radiation</Code> — corrisponde a DNI (opzionale).</li>
              <li><Code>temperature_2m</Code> — temperatura dell'aria (°C).</li>
              <li><Code>cloud_cover</Code> — copertura nuvolosa (%).</li>
              <li><Code>wind_speed_10m</Code> — velocità del vento a 10 m (m/s).</li>
              <li><Code>wind_direction_10m</Code> — direzione del vento (gradi).</li>
            </ul>
          </li>
          <li>Imposta il formato su <strong>CSV</strong> e clicca <strong>Download</strong>.</li>
          <li>Apri il file. Rinomina le colonne secondo i nomi attesi dalla piattaforma:
            {' '}<Code>shortwave_radiation</Code> → <Code>ghi_w_m2</Code>,
            {' '}<Code>direct_radiation</Code> → <Code>dni_w_m2</Code>,
            {' '}<Code>temperature_2m</Code> → <Code>temperature_c</Code>,
            {' '}<Code>cloud_cover</Code> → <Code>cloud_cover_pct</Code>,
            {' '}<Code>wind_speed_10m</Code> → <Code>wind_speed_m_s</Code>,
            {' '}<Code>wind_direction_10m</Code> → <Code>wind_direction_deg</Code>.</li>
          <li>Assicurati che la colonna del timestamp si chiami <Code>timestamp</Code>. Salva come CSV UTF-8 e
            carica il file.</li>
        </ol>

        <h6 className="mt-3">PVGIS — Commissione Europea JRC (ideale per la pianificazione solare)</h6>
        <p>
          <Ext href="https://re.jrc.ec.europa.eu/pvg_tools/en/">re.jrc.ec.europa.eu/pvg_tools/en/</Ext>
          {' '}— gratuito, senza registrazione, copre tutta Europa. Particolarmente utile quando si
          ha bisogno di un anno «tipico» di riferimento (TMY) anziché uno storico specifico.
        </p>
        <ol>
          <li>Apri il sito e seleziona la tua posizione sulla mappa.</li>
          <li>Scegli <strong>«Hourly data»</strong> nelle schede a sinistra.</li>
          <li>Seleziona <strong>Solar radiation components</strong> e <strong>2-m air temperature</strong>,
            scegli un anno (o un intervallo) e clicca <strong>Download CSV</strong>.</li>
          <li>Il file scaricato contiene una piccola intestazione che potrebbe essere necessario eliminare
            affinché la riga delle colonne
            (<Code>time</Code>, <Code>G(i)</Code>, <Code>T2m</Code>, …) si trovi sulla riga 1. Rinomina
            {' '}<Code>G(i)</Code> → <Code>ghi_w_m2</Code> e <Code>T2m</Code> → <Code>temperature_c</Code>.</li>
        </ol>
        <Tip>I timestamp di PVGIS sono solitamente in UTC e possono avere il formato <Code>20250101:1000</Code>
          — convertili in <Code>2025-01-01T10:00:00Z</Code> usando la formula descritta in
          {' '}<ArticleLink to="data-csv-basics">Creare file CSV correttamente</ArticleLink>.</Tip>

        <h6 className="mt-3">Renewables.ninja (produzione solare/eolica specifica per posizione)</h6>
        <p>
          <Ext href="https://www.renewables.ninja">renewables.ninja</Ext> — gratuito con una rapida
          registrazione. Inserisci le coordinate, scegli «MERRA-2» o «ERA-5», anno e risoluzione, e
          scarica il CSV. La variabile da mappare è <Code>irradiance</Code> → <Code>ghi_w_m2</Code> per
          il solare, e <Code>wind_speed</Code> → <Code>wind_speed_m_s</Code> per il vento.
        </p>

        <h6 className="mt-3">NASA POWER (alternativa globale)</h6>
        <p>
          <Ext href="https://power.larc.nasa.gov/data-access-viewer/">power.larc.nasa.gov/data-access-viewer</Ext>
          {' '}— dati orari globali, gratuiti. Utile quando le altre fonti non coprono la posizione.
          Nomi delle variabili: <Code>ALLSKY_SFC_SW_DWN</Code> → <Code>ghi_w_m2</Code>,
          {' '}<Code>T2M</Code> → <Code>temperature_c</Code>,
          {' '}<Code>WS10M</Code> → <Code>wind_speed_m_s</Code>.
        </p>

        <h6 className="mt-3">Servizi meteorologici nazionali</h6>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Paese</th><th>Servizio</th></tr>
          </thead>
          <tbody>
            <tr><td>Italia</td><td>ISPRA / ARPA regionali (es. ARPA Lombardia, ARPA Veneto) — dati orari per stazione, per regione.</td></tr>
            <tr><td>Francia</td><td>Portale open data di Météo-France (<Ext href="https://meteo.data.gouv.fr">meteo.data.gouv.fr</Ext>) — osservazioni orarie storiche.</td></tr>
            <tr><td>Germania</td><td>DWD Climate Data Center (<Ext href="https://opendata.dwd.de">opendata.dwd.de</Ext>) — gratuito, dati orari a livello di stazione.</td></tr>
            <tr><td>Rep. Ceca</td><td>CHMI (<Ext href="https://www.chmi.cz">chmi.cz</Ext>) — dati storici per stazione su richiesta o tramite portale.</td></tr>
            <tr><td>Grecia</td><td>HNMS / NOA-Meteo (<Ext href="https://www.meteo.gr">meteo.gr</Ext>) — dati storici gratuiti limitati; Open-Meteo è di solito più comodo.</td></tr>
          </tbody>
        </Table>

        <Warn>
          Assicurati che l'<strong>anno</strong> e il <strong>fuso orario</strong> del file meteo
          corrispondano agli altri file. Se i dati di carico si riferiscono al 2024 in ora locale,
          scarica i dati meteo del 2024 in UTC e convertili (Open-Meteo permette di scegliere il
          fuso orario prima del download). Anni o fusi orari non corrispondenti peggiorano le
          previsioni, anziché migliorarle.
        </Warn>
      </>
    ),
  },

  {
    id: 'data-bill',
    category: 'data',
    title: 'File bollette energetiche',
    summary: 'Dati di fatturazione mensile utilizzati per l\'analisi dei costi.',
    body: (
      <>
        <p>
          Un file <strong>Bolletta Energetica</strong> elenca le bollette passate, una riga per
          periodo di fatturazione (di solito un mese). Viene utilizzato per l'analisi dei costi.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonna</th><th>Obbligatoria?</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>period_start</Code></td><td>Obbligatoria</td><td>Primo giorno del periodo di fatturazione, es. <Code>2025-01-01</Code></td></tr>
            <tr><td><Code>period_end</Code></td><td>Obbligatoria</td><td>Ultimo giorno del periodo di fatturazione, es. <Code>2025-01-31</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Obbligatoria</td><td>Totale energia consumata nel periodo, in kWh</td></tr>
            <tr><td><Code>cost_eur</Code></td><td>Obbligatoria</td><td>Importo totale pagato, in euro</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Opzionale</td><td>Massimo prelievo di potenza nel periodo, in kW</td></tr>
            <tr><td><Code>rate_eur_per_kwh</Code></td><td>Opzionale</td><td>Prezzo medio per kWh</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`period_start,period_end,consumption_kwh,cost_eur
2025-01-01,2025-01-31,3850,962.50
2025-02-01,2025-02-28,3520,880.00`}
        </pre>
        <TemplateBtn type="building_energy_bill">Scarica il template bolletta energetica</TemplateBtn>
        <Warn>La data di inizio di ogni periodo deve essere precedente alla data di fine e i periodi non devono sovrapporsi.</Warn>
      </>
    ),
  },
]
